import { PredefinedPatternKey, predefinedPatterns } from './patterns';

function validateChoice<T>(value: T, choices?: readonly T[]): void {
  if (choices && !choices.includes(value)) {
    throw new Error(`Invalid value "${value}". Must be one of: ${choices.join(', ')}`);
  }
}

export type EnvVarType = 'string' | 'number' | 'boolean';

type MakeVarOptionsBase = {
  required: boolean;
  mask?: boolean;
  match?: RegExp | PredefinedPatternKey;
  minLength?: number;
  maxLength?: number;
};

type MakeVarOptions<T, C extends readonly T[] | undefined = undefined> = MakeVarOptionsBase & {
  choices?: C;
  default?: C extends readonly T[] ? C[number] : T;
};

export interface EnvVarSpec<T> {
  type: EnvVarType;
  default?: T;
  required?: boolean;
  mask?: boolean;
  match?: RegExp | keyof typeof predefinedPatterns;
  minLength?: number;
  maxLength?: number;
  choices?: T[];
}

type InferVarReturnType<
  T,
  R extends boolean,
  C extends readonly T[] | undefined,
  D,
> = D extends undefined
  ? R extends false
    ? C extends readonly T[]
      ? C[number] | undefined
      : T | undefined
    : C extends readonly T[]
      ? C[number]
      : T
  : C extends readonly T[]
    ? C[number]
    : T;

export function makeVar<
  T,
  C extends readonly T[] | undefined,
  R extends boolean,
  D = C extends readonly T[] ? C[number] : T,
>(
  type: 'string' | 'number' | 'boolean',
  options: MakeVarOptions<T, C> & { required: R; default?: D }
): EnvVarSpec<InferVarReturnType<T, R, C, D>> {
  const { required, default: def, choices, mask, match, minLength, maxLength } = options;

  if (def !== undefined) {
    validateChoice(def, choices);
  }

  const spec: EnvVarSpec<InferVarReturnType<T, R, C, D>> = {
    type,
    required,
    default: def,
    choices: choices as InferVarReturnType<T, R, C, D>[], // safe coercion
    mask,
    match,
    minLength,
    maxLength,
  };

  return spec;
}

// Public API
export const makeStringVar = <
  const C extends readonly string[] | undefined = undefined,
  const R extends boolean = boolean,
  const D = undefined,
>(
  options: MakeVarOptions<string, C> & { required: R; default?: D }
): EnvVarSpec<InferVarReturnType<string, R, C, D>> => makeVar<string, C, R, D>('string', options);

export const makeNumberVar = <
  const C extends readonly number[] | undefined,
  const R extends boolean,
>(
  options: MakeVarOptions<number, C> & { required: R }
) => makeVar<number, C, R>('number', options);

export const makeBooleanVar = <
  const C extends readonly boolean[] | undefined,
  const R extends boolean,
>(
  options: MakeVarOptions<boolean, C> & { required: R }
) => makeVar<boolean, C, R>('boolean', options);

export function defineEnvSchema<const T extends Record<string, EnvVarSpec<unknown>>>(schema: T): T {
  return schema;
}
